import {
	FACEBOOK_PIXEL_CUSTOM_EVENT,
	FACEBOOK_PIXEL_EVENT,
	GOOGLE_TAG_MANAGER_EVENT,
	GoogleTagManagerEventPayloadMap,
	FacebookPixelEventPayloadMap,
	GTMItem,
} from '../types';
import { googleTagManagerEvent } from './googleTagManager';
import { pixelEvent } from './pixelEvent';

const EVENT_MAPPING = {
	[GOOGLE_TAG_MANAGER_EVENT.ADD_TO_CART]: FACEBOOK_PIXEL_EVENT.ADD_TO_CART,
	[GOOGLE_TAG_MANAGER_EVENT.BEGIN_CHECKOUT]: FACEBOOK_PIXEL_EVENT.BEGIN_CHECKOUT,
	[GOOGLE_TAG_MANAGER_EVENT.PURCHASE]: FACEBOOK_PIXEL_EVENT.PURCHASE,
	[GOOGLE_TAG_MANAGER_EVENT.VIEW_ITEM]: FACEBOOK_PIXEL_EVENT.VIEW_CONTENT,
	[GOOGLE_TAG_MANAGER_EVENT.SELECT_ITEM]: FACEBOOK_PIXEL_CUSTOM_EVENT.SELECT_ITEM,
	[GOOGLE_TAG_MANAGER_EVENT.VIEW_CART]: FACEBOOK_PIXEL_CUSTOM_EVENT.VIEW_CART,
	[GOOGLE_TAG_MANAGER_EVENT.REMOVE_FROM_CART]: FACEBOOK_PIXEL_CUSTOM_EVENT.REMOVE_FROM_CART,
} as const;

const hasGTMSKU = (item: GTMItem): item is GTMItem & { sku: string } => Boolean(item.sku);

const hasGTMItemId = (item: GTMItem): item is GTMItem & { item_id: string } => Boolean(item.item_id);

const hasGTMSlug = (item: GTMItem): item is GTMItem & { slug: string } => Boolean(item.slug);

const hasGTMTitle = (item: GTMItem): item is GTMItem & { title: string } => Boolean(item.title);

const extractItemDetails = (item: GTMItem) => {
	let itemId = '';

	if (hasGTMSKU(item)) {
		itemId = item.sku;
	} else if (hasGTMItemId(item)) {
		itemId = item.item_id;
	} else if (hasGTMSlug(item)) {
		itemId = item.slug;
	} else if (hasGTMTitle(item)) {
		itemId = item.title;
	}

	return {
		id: itemId,
		quantity: item.quantity || 1,
		item_price: item.price || 0,
	};
};

type FacebookPixelTransformResult =
	| FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_EVENT.ADD_TO_CART]
	| FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_EVENT.BEGIN_CHECKOUT]
	| FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_EVENT.PURCHASE]
	| FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_EVENT.VIEW_CONTENT]
	| FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_CUSTOM_EVENT.SELECT_ITEM]
	| FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_CUSTOM_EVENT.VIEW_CART]
	| FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_CUSTOM_EVENT.REMOVE_FROM_CART];

const transformPayloadForPixel = <K extends keyof typeof EVENT_MAPPING>(
	gtmEventName: K,
	gtmPayload: GoogleTagManagerEventPayloadMap[K],
): FacebookPixelTransformResult => {
	const basePayload = {
		content_type: 'product' as const,
		currency: gtmPayload && typeof gtmPayload === 'object' && 'currency' in gtmPayload
			? gtmPayload.currency.toUpperCase()
			: '',
	};

	switch (gtmEventName) {
	case GOOGLE_TAG_MANAGER_EVENT.ADD_TO_CART:
	case GOOGLE_TAG_MANAGER_EVENT.BEGIN_CHECKOUT:
	case GOOGLE_TAG_MANAGER_EVENT.PURCHASE: {
		if (!gtmPayload || typeof gtmPayload !== 'object') {
			const fallbackResult: FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_EVENT.ADD_TO_CART] = {
				...basePayload,
				value: 0,
				content_ids: [],
				contents: [],
				num_items: 0,
			};

			return fallbackResult;
		}

		const payload = gtmPayload as GoogleTagManagerEventPayloadMap[typeof GOOGLE_TAG_MANAGER_EVENT.ADD_TO_CART] |
			GoogleTagManagerEventPayloadMap[typeof GOOGLE_TAG_MANAGER_EVENT.BEGIN_CHECKOUT] |
			GoogleTagManagerEventPayloadMap[typeof GOOGLE_TAG_MANAGER_EVENT.PURCHASE];

		const contents = payload.items?.map((item) => extractItemDetails(item)) || [];

		const result: FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_EVENT.PURCHASE] = {
			...basePayload,
			value: payload.value || 0,
			content_ids: contents.map((item) => item.id),
			contents,
			num_items: contents.length,
		};

		if ('transaction_id' in payload && payload.transaction_id) {
			result.order_id = payload.transaction_id;
		}

		if ('shipping' in payload && payload.shipping) {
			result.shipping = payload.shipping;
		}

		if ('tax' in payload && payload.tax) {
			result.tax = payload.tax;
		}

		return result;
	}

	case GOOGLE_TAG_MANAGER_EVENT.VIEW_ITEM:
	case GOOGLE_TAG_MANAGER_EVENT.SELECT_ITEM: {
		if (!gtmPayload || typeof gtmPayload !== 'object') {
			const fallbackResult: FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_EVENT.VIEW_CONTENT] = {
				...basePayload,
				content_ids: [],
				content_name: '',
				contents: [],
			};

			return fallbackResult;
		}

		const payload = gtmPayload as GoogleTagManagerEventPayloadMap[typeof GOOGLE_TAG_MANAGER_EVENT.VIEW_ITEM] |
			GoogleTagManagerEventPayloadMap[typeof GOOGLE_TAG_MANAGER_EVENT.SELECT_ITEM];

		const contents = payload.items?.map((item) => extractItemDetails(item)) || [];

		const result: FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_EVENT.VIEW_CONTENT] = {
			...basePayload,
			content_ids: contents.map((item) => item.id),
			content_name: payload.items?.[0]?.item_name || '',
			contents,
		};

		if (payload.items?.[0]?.price) {
			result.value = payload.items[0].price;
		}

		return result;
	}

	case GOOGLE_TAG_MANAGER_EVENT.VIEW_CART:
	case GOOGLE_TAG_MANAGER_EVENT.REMOVE_FROM_CART: {
		if (!gtmPayload || typeof gtmPayload !== 'object') {
			const fallbackResult: FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_CUSTOM_EVENT.VIEW_CART] = {
				...basePayload,
				value: 0,
				content_ids: [],
				contents: [],
				num_items: 0,
			};

			return fallbackResult;
		}

		const payload = gtmPayload as GoogleTagManagerEventPayloadMap[typeof GOOGLE_TAG_MANAGER_EVENT.VIEW_CART] |
			GoogleTagManagerEventPayloadMap[typeof GOOGLE_TAG_MANAGER_EVENT.REMOVE_FROM_CART];

		const contents = payload.items?.map((item) => extractItemDetails(item)) || [];

		const result: FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_CUSTOM_EVENT.REMOVE_FROM_CART] = {
			...basePayload,
			value: payload.value || 0,
			content_ids: contents.map((item) => item.id),
			content_name: payload.items?.map((item) => item.item_name).join(', ') || '',
			contents,
			num_items: contents.length,
		};

		return result;
	}

	default: {
		const result: FacebookPixelEventPayloadMap[typeof FACEBOOK_PIXEL_EVENT.ADD_TO_CART] = {
			...basePayload,
			value: 0,
			content_ids: [],
			contents: [],
			num_items: 0,
		};

		return result;
	}
	}
};

/**
 * Unified analytics event function that fires both Google Tag Manager and Facebook Pixel events
 * @param eventName - The Google Tag Manager event name (used as the primary event type)
 * @param payload - The event payload following GTM structure
 */
export const analyticsEvent = <K extends keyof GoogleTagManagerEventPayloadMap>(
	eventName: K,
	payload?: GoogleTagManagerEventPayloadMap[K],
) => {
	if (typeof window?.gtag === 'function') {
		googleTagManagerEvent(eventName, payload);
	}

	if (!window?.fbq) {
		return;
	}

	const pixelEventName = EVENT_MAPPING[eventName as keyof typeof EVENT_MAPPING];

	if (pixelEventName && payload && eventName in EVENT_MAPPING) {
		const mappedEventName = eventName as keyof typeof EVENT_MAPPING;
		const mappedPayload = payload as GoogleTagManagerEventPayloadMap[keyof typeof EVENT_MAPPING];
		const transformedPayload = transformPayloadForPixel(
			mappedEventName,
			mappedPayload,
		);

		pixelEvent(pixelEventName, transformedPayload);
	}
};
